import * as Sentry from '@sentry/capacitor';
import * as SentryAngular from '@sentry/angular';
import { environment } from '../environments/environment';
import { AppBuild, isLocalBuild, stampBuild } from './sentry-release';
import { anEscapedHttpFailureHasAName } from './an-escaped-http-failure-has-a-name';
import { aBreadcrumbNamesNoOne } from './a-breadcrumb-names-no-one';
import { usageMeasurementIsAllowed } from './core/domains/app/domains/the-usage-measurement-switch';

// Error monitoring, and a sample of start-up timings.
//
// What changed and why:
//
// - The DSN was hardcoded and pointed at organisation o4508630648684544, an
//   account that is no longer accessible. The mobile app has therefore been
//   sending its errors nowhere. It now comes from the environment, like the
//   API base URL, so it can never silently rot in code again.
//
// - replayIntegration() was capturing 100% of sessions where an error occurred
//   (replaysOnErrorSampleRate: 1.0). On an end-user app that records what
//   thousands of people see and do, which is personal data processing and was
//   never a deliberate decision. Session Replay is also not enabled on the
//   Sentry plan. Removed. If it is ever wanted, it needs field masking, a
//   privacy policy update and a legal basis first.
//
// - browserTracingIntegration() and tracesSampleRate: 1.0 sent transactions
//   that the plan does not accept. Removed. PUT BACK ON 22 SEPTEMBER 2026 at
//   a sample, for one question nobody could answer: how long does the
//   application take to start? Maude asked it while deciding whether the
//   launch screen needs a spinner, and the honest answer was that nothing in
//   the repository or in Sentry measures it, on any phone, ever. A decision
//   about what somebody stares at for that time should not be taken blind.
//
// - tracePropagationTargets listed ondigitalocean.app domains from a previous
//   host. It now names our API and nothing else: these headers must not be
//   attached to third parties.
//
// Native crash capture is ON since 30 September 2026, and the reason it was off
// was never the flag. @sentry/capacitor ships a real Capacitor plugin, with its
// own podspec and android sources, and it was registered in NEITHER native
// project: 24 pods on iOS and 20 plugins on Android, Sentry in none of them.
// Turning the flag on without linking the plugin captures nothing and fails at
// start-up, so `false` was the honest state of the world rather than an
// oversight. The generated native files in this change are what links it.
//
// WHAT THIS ADDS, AND WHAT IT DOES NOT. JavaScript errors were already
// reported and reported well: the Explore crash of this same morning
// arrived 998 times with its release and its build number attached. What was
// invisible is everything that kills the application outside the webview -- a
// plugin that crashes, a death by memory, and above all a crash at start-up
// before any JavaScript runs, which is the one nothing else can see.
//
// STILL MISSING: dSYM upload. Without it an iOS native crash arrives as memory
// addresses rather than function names. That needs a Sentry auth token as a CI
// secret and belongs in the fastlane lane, not here.
//
// - Events carried no release, so nothing said which build an error came from.
//   That is the fact the keychain ticket (CAPACITOR-3) is waiting on: the same
//   error is an artefact on a reinstalled development build and a real failure
//   to store the session token on an App Store build. The version is only
//   readable asynchronously through Capacitor, so the SDK starts now and
//   events are stamped on their way out. See sentry-release.ts.
const appBuild: AppBuild = {};
void Promise.all([environment.version, environment.buildNumber])
  .then(([version, build]) => {
    appBuild.version = version;
    appBuild.build = build;
  })
  // A version we cannot read is not worth failing a startup for. Events simply
  // go out unstamped, as they did before.
  .catch(() => undefined);

Sentry.init(
  {
    dsn: environment.sentryDsn,
    environment: environment.production ? 'production' : 'development',
    integrations: [SentryAngular.browserTracingIntegration()],
    /*
     * One start in five, not all of them.
     *
     * The rate is set for the SAMPLE SIZE, not to spare the quota: this
     * community is small, and at one in a hundred the question would still be
     * unanswered in a month. One line to lower once the picture is clear, and
     * the picture is a distribution rather than an average: a mean start time
     * hides the old Android phone that waits five seconds, which is the only
     * phone the question was ever about.
     */
    tracesSampleRate: 0.2,
    /*
     * Trace headers go to our API and nowhere else. A wider list attaches them
     * to third parties, which is somebody else's server being told about our
     * traces.
     */
    tracePropagationTargets: [environment.api],
    enableNative: true,
    /*
     * The iOS « App Hanging » report is kept for the builds members install,
     * and off for the development ones.
     *
     * Found on 22 October 2026 (CAPACITOR-19 and CAPACITOR-1A): the first four
     * hangs Sentry ever received all came from the iOS simulator on the
     * development Mac, build 2.2.6 (128), one of them in an application started
     * nineteen hours earlier. A simulator stalls whenever its Mac sleeps or
     * gets busy, and a development build on a phone is often paused in the
     * debugger: both look exactly like a frozen screen and are not one.
     *
     * This cannot be filtered in beforeSend like the laptop errors above: a
     * hang is captured by the native layer, and @sentry/capacitor replaces the
     * native beforeSend with its own, so these events never pass through the
     * JavaScript hooks. The switch is the only lever. The 2-second threshold
     * stays Sentry's default on production builds.
     */
    enableAppHangTracking: environment.production,
    // Errors from a developer's own machine are dropped rather than sent.
    //
    // Sentry is worth having for the errors nobody can see. A developer
    // running the application on their laptop has the console open in front of
    // them, and what they send instead drowns what members report: on 18
    // September the network crash showed 30 events over 11 « users », all of
    // them development sessions on localhost in Chrome on a Mac, next to three
    // real reports from real phones. Two of those issues were preview routes
    // that existed for an afternoon.
    //
    // A build installed on a device still reports, whatever its environment: a
    // development build on a real phone is exactly what the keychain report of
    // 13 September was, and it was worth having.
    //
    // The version arrives one tick after start-up, so an error in that first
    // tick still goes out from a laptop. That is a handful of events and the
    // alternative, holding the SDK back until the version is known, would lose
    // crashes at the one moment nothing else is watching.
    /*
     * An HTTP failure that escaped uncaught is given its name on the way out.
     * Without it, every one of them arrives as « Object captured as exception
     * with keys: error, headers, message, name, ok », in a single bucket that
     * names no endpoint and no status. See
     * an-escaped-http-failure-has-a-name.ts, and CAPACITOR-T.
     */
    /*
     * The trail attached to an error keeps its screens and endpoints, never
     * the ids, tokens and emails in them. See a-breadcrumb-names-no-one.ts.
     */
    beforeBreadcrumb: aBreadcrumbNamesNoOne,
    beforeSend: (event) => (isLocalBuild(appBuild) ? null : anEscapedHttpFailureHasAName(stampBuild(event, appBuild))),
    /*
     * Timings follow the same rule as errors: a developer's laptop is not a
     * member's phone, and mixing the two would answer the question with the
     * wrong machines. This is the lesson of 18 September written once more, in
     * the place where it would otherwise be missed.
     *
     * A device that refused the usage measurement sends no timing at all.
     * Found on 16 October 2026 (Asana 1219575473860443): the refusal stopped
     * Firebase and nothing else, while a start-up timing is a measurement of
     * use like any other. Read here, at send time, so the switch counts from
     * the moment it moves rather than from the next start. Errors are not
     * held back: they are how a crash on somebody's phone becomes visible,
     * and the privacy policy says they are kept on that ground.
     */
    beforeSendTransaction: (event) =>
      isLocalBuild(appBuild) || !usageMeasurementIsAllowed() ? null : stampBuild(event, appBuild),
  },
  // Forward the init method from @sentry/angular
  SentryAngular.init,
);

export { captureException, captureMessage, captureEvent } from '@sentry/capacitor';
